import { nanoid } from 'nanoid';
import { setStoryEditorUI } from 'admin/actions/story/set-story-editor-ui';
import { IS_AI_AND_BRAND_KIT_ENABLED } from 'admin/constants/release-features';
import { useCallback, useEffect, useRef } from 'react';
import type { MemberChatPreferences } from 'src/types/user';
import { setMemberPreferences } from 'admin/actions/member/preferences';
import { setStoryEditorChat } from 'admin/actions/story/set-story-editor-chat';
import { useAdminDispatch, useAdminSelector } from 'admin/reducers';
import { selectStoryEditorChatDraft, selectStoryEditorChatOpen } from 'admin/reducers/story-editor/selectors';

/**
 * State and actions for the story editor chat interface.
 */
export const useStoryEditorChatState = () => {
	const dispatch = useAdminDispatch();
	const isOpen = useAdminSelector(selectStoryEditorChatOpen);
	const draft = useAdminSelector(selectStoryEditorChatDraft);
	const chatPreferences = useAdminSelector(state => state.user.preferences.chat);
	const chatPreferencesRef = useRef(chatPreferences);
	useEffect(() => {
		chatPreferencesRef.current = chatPreferences;
	}, [chatPreferences]);
	const updateChatPreferences = useCallback(
		(patch: Partial<MemberChatPreferences>) => {
			const next = { ...chatPreferencesRef.current, ...patch };
			chatPreferencesRef.current = next;
			dispatch(setMemberPreferences({ preferences: { chat: next } }));
		},
		[dispatch]
	);
	const isEnabled = IS_AI_AND_BRAND_KIT_ENABLED;
	const onOpenChange = useCallback(
		(nextOpen: boolean) => {
			if (!IS_AI_AND_BRAND_KIT_ENABLED) return;
			dispatch(setStoryEditorChat({ open: nextOpen }));
			updateChatPreferences({ open: nextOpen });
		},
		[dispatch, updateChatPreferences]
	);
	const openWithDraft = useCallback(
		(text: string) => {
			if (!IS_AI_AND_BRAND_KIT_ENABLED) return;
			dispatch(setStoryEditorChat({ open: true, draft: { id: `${Date.now()}`, text } }));
			updateChatPreferences({ open: true });
		},
		[dispatch, updateChatPreferences]
	);

	const openWithMention = useCallback(
		(storyId: string, elementId: string) => {
			if (!IS_AI_AND_BRAND_KIT_ENABLED) return;
			dispatch(setStoryEditorChat({ open: true, mention: { id: nanoid(), storyId, elementId } }));
			dispatch(setStoryEditorUI({ focusMode: false }));
			updateChatPreferences({ open: true });
		},
		[dispatch, updateChatPreferences]
	);

	return {
		isOpen: isEnabled && isOpen,
		isEnabled,
		draft,
		chatPreferences,
		onOpenChange,
		openWithDraft,
		openWithMention,
		updateChatPreferences,
	};
};
